import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { useSelector } from "react-redux";
import { ThemeProvider as MuiThemeProvider } from "@material-ui/core/styles";
import { createAppTheme, ThemeMode, CustomPalette, Theme } from "src/theme";
import {
  themeModeSelector,
  themeUseCustomPaletteSelector,
  themeCustomLightPaletteSelector,
  themeCustomDarkPaletteSelector,
} from "src/store/system/selector";
import {
  updateThemePreferencesApi,
  resetThemePreferencesApi,
} from "src/apiService/modules/system";
import { toast } from "react-toastify";

// ============================================
// TYPES
// ============================================
export interface ThemePreferences {
  mode: ThemeMode;
  useCustomPalette: boolean;
  customLightPalette: CustomPalette;
  customDarkPalette: CustomPalette;
}

export interface ThemeContextValue {
  // Current state
  mode: ThemeMode;
  theme: Theme;
  preferences: ThemePreferences;

  // Actions
  toggleMode: () => void;
  setMode: (mode: ThemeMode) => void;
  setCustomLightPalette: (palette: Partial<CustomPalette>) => void;
  setCustomDarkPalette: (palette: Partial<CustomPalette>) => void;
  setUseCustomPalette: (use: boolean) => void;
  resetToDefaults: () => void;

  // Utility
  isDark: boolean;
}

// ============================================
// CONTEXT
// ============================================
const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// ============================================
// PROVIDER
// ============================================
interface ThemeProviderProps {
  children: ReactNode;
  initialPreferences?: Partial<ThemePreferences>;
}

export function ThemeContextProvider({
  children,
  initialPreferences,
}: ThemeProviderProps) {
  // Read preferences directly from Redux
  const mode = useSelector(themeModeSelector);
  const useCustomPalette = useSelector(themeUseCustomPaletteSelector);
  const customLightPalette = useSelector(themeCustomLightPaletteSelector);
  const customDarkPalette = useSelector(themeCustomDarkPaletteSelector);

  // Derive preferences object
  const preferences: ThemePreferences = useMemo(
    () => ({
      mode,
      useCustomPalette,
      customLightPalette,
      customDarkPalette,
    }),
    [mode, useCustomPalette, customLightPalette, customDarkPalette]
  );

  const isDark = mode === "dark";

  // Create the theme based on current preferences
  const theme = useMemo(() => {
    const currentPalette = isDark ? customDarkPalette : customLightPalette;

    if (useCustomPalette) {
      return createAppTheme(mode, currentPalette, customDarkPalette.bg);
    }

    return createAppTheme(mode);
  }, [mode, isDark, useCustomPalette, customLightPalette, customDarkPalette]);

  // Helper function to update preferences via API
  const updatePreferences = useCallback(
    (newPreferences: Partial<ThemePreferences>) => {
      // Call API directly with only the changed values
      updateThemePreferencesApi(newPreferences).catch((error) => {
        console.error("Failed to update theme preferences:", error);
        toast.error("Failed to update theme preferences");
      });
    },
    []
  );

  // Toggle between light and dark mode
  const toggleMode = useCallback(() => {
    updatePreferences({
      mode: mode === "light" ? "dark" : "light",
    });
  }, [mode, updatePreferences]);

  // Set specific mode
  const setMode = useCallback(
    (newMode: ThemeMode) => {
      updatePreferences({
        mode: newMode,
      });
    },
    [updatePreferences]
  );

  // Update custom light palette
  const setCustomLightPalette = useCallback(
    (palette: Partial<CustomPalette>) => {
      updatePreferences({
        customLightPalette: palette as any,
      });
    },
    [updatePreferences]
  );

  // Update custom dark palette
  const setCustomDarkPalette = useCallback(
    (palette: Partial<CustomPalette>) => {
      updatePreferences({
        customDarkPalette: palette as any,
      });
    },
    [updatePreferences]
  );

  // Toggle custom palette usage
  const setUseCustomPalette = useCallback(
    (use: boolean) => {
      updatePreferences({
        useCustomPalette: use,
      });
    },
    [updatePreferences]
  );

  // Reset all preferences to defaults
  const resetToDefaults = useCallback(() => {
    resetThemePreferencesApi().catch((error) => {
      console.error("Failed to reset theme preferences:", error);
      toast.error("Failed to reset theme preferences");
    });
  }, []);

  // Context value
  const contextValue: ThemeContextValue = useMemo(
    () => ({
      mode,
      theme,
      preferences,
      toggleMode,
      setMode,
      setCustomLightPalette,
      setCustomDarkPalette,
      setUseCustomPalette,
      resetToDefaults,
      isDark,
    }),
    [
      mode,
      theme,
      preferences,
      toggleMode,
      setMode,
      setCustomLightPalette,
      setCustomDarkPalette,
      setUseCustomPalette,
      resetToDefaults,
      isDark,
    ]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

// ============================================
// HOOKS
// ============================================

/**
 * Hook to access the theme context
 */
export function useThemeContext(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error(
      "useThemeContext must be used within a ThemeContextProvider"
    );
  }
  return context;
}

/**
 * Hook to get current theme mode
 */
export function useThemeMode(): ThemeMode {
  const { mode } = useThemeContext();
  return mode;
}

/**
 * Hook to toggle theme mode
 */
export function useToggleTheme(): () => void {
  const { toggleMode } = useThemeContext();
  return toggleMode;
}

/**
 * Hook to check if dark mode is active
 */
export function useIsDarkMode(): boolean {
  const { isDark } = useThemeContext();
  return isDark;
}

/**
 * Hook to access custom palette setters
 */
export function useCustomPalette() {
  const {
    preferences,
    setCustomLightPalette,
    setCustomDarkPalette,
    setUseCustomPalette,
    resetToDefaults,
  } = useThemeContext();

  return {
    useCustomPalette: preferences.useCustomPalette,
    customLightPalette: preferences.customLightPalette,
    customDarkPalette: preferences.customDarkPalette,
    setCustomLightPalette,
    setCustomDarkPalette,
    setUseCustomPalette,
    resetToDefaults,
  };
}

export default ThemeContext;
